import { cx } from '~/ui/cx'
import boardAvif1600 from './board-1600.avif'
import boardWebp1600 from './board-1600.webp'
import boardAvif900 from './board-900.avif'
import boardWebp900 from './board-900.webp'
import heroAvif1400 from './hero-1400.avif'
import heroWebp1400 from './hero-1400.webp'
import heroAvif2400 from './hero-2400.avif'
import heroWebp2400 from './hero-2400.webp'
import netAvif1200 from './net-1200.avif'
import netWebp1200 from './net-1200.webp'
import netAvif700 from './net-700.avif'
import netWebp700 from './net-700.webp'
import nightAvif1400 from './night-1400.avif'
import pitchHAvif1470 from './pitch-h-1470.avif'
import pitchHWebp1470 from './pitch-h-1470.webp'
import pitchHAvif735 from './pitch-h-735.avif'
import pitchHWebp735 from './pitch-h-735.webp'
import pitchVAvif476 from './pitch-v-476.avif'
import pitchVWebp476 from './pitch-v-476.webp'
import pitchVAvif952 from './pitch-v-952.avif'
import pitchVWebp952 from './pitch-v-952.webp'
import nightWebp1400 from './night-1400.webp'
import nightAvif2400 from './night-2400.avif'
import nightWebp2400 from './night-2400.webp'
import trainingAvif1600 from './training-1600.avif'
import trainingWebp1600 from './training-1600.webp'
import trainingAvif900 from './training-900.avif'
import trainingWebp900 from './training-900.webp'
import turfAvif1400 from './turf-1400.avif'
import turfWebp1400 from './turf-1400.webp'
import turfAvif2400 from './turf-2400.avif'
import turfWebp2400 from './turf-2400.webp'

// Rendered by apps/web/scripts/art.py (OpenCV, procedural). Each motif in two widths, AVIF with a
// WebP fallback; width/height are the large file's, so the box is reserved before the bytes arrive.

export type Art = { avif: string; webp: string; src: string; width: number; height: number }

const motif = (small: [number, string, string], large: [number, string, string], height: number): Art => ({
  avif: `${small[1]} ${small[0]}w, ${large[1]} ${large[0]}w`,
  webp: `${small[2]} ${small[0]}w, ${large[2]} ${large[0]}w`,
  src: small[2],
  width: large[0],
  height,
})

/** Floodlit pitch from the main camera position. heroScene.ts knows where the formation stands in it. */
export const HERO = motif([1400, heroAvif1400, heroWebp1400], [2400, heroAvif2400, heroWebp2400], 1200)
/** Ball on the touchline at night, floodlights out of focus on the right. The left half is quiet for text. */
export const NIGHT = motif([1400, nightAvif1400, nightWebp1400], [2400, nightAvif2400, nightWebp2400], 1000)
/** A training drill seen by a drone, low sun. */
export const TRAINING = motif([900, trainingAvif900, trainingWebp900], [1600, trainingAvif1600, trainingWebp1600], 1200)
/** Tactics in chalk on the dressing-room board. */
export const BOARD = motif([900, boardAvif900, boardWebp900], [1600, boardAvif1600, boardWebp1600], 1000)
/** Through the back net of a goal at night: crossbar, post and the hero's pitch out of focus behind. */
export const NET = motif([700, netAvif700, netWebp700], [1200, netAvif1200, netWebp1200], 1500)
/** The whole pitch from straight above, grass only (105 × 68 m, landscape and portrait): ui/Pitch draws the lines. */
export const PITCH_H = motif([735, pitchHAvif735, pitchHWebp735], [1470, pitchHAvif1470, pitchHWebp1470], 952)
export const PITCH_V = motif([476, pitchVAvif476, pitchVWebp476], [952, pitchVAvif952, pitchVWebp952], 1470)
/** Turf from straight above with a painted line and three circles, at 1/6, 3/6 and 5/6 of the width:
 *  the centres of a three-column grid laid over the uncropped picture (aspect 15:4). */
export const TURF = motif([1400, turfAvif1400, turfWebp1400], [2400, turfAvif2400, turfWebp2400], 640)

type PictureProps = {
  art: Art
  /** The rendered width, for picking a file: "(min-width: 1024px) 1100px, 100vw". */
  sizes: string
  /** The first picture on the page: load it now and before everything else. */
  priority?: boolean
  className?: string
}

/** Decorative: every motif here illustrates text that stands next to it. */
export function Picture({ art, sizes, priority, className }: PictureProps) {
  return (
    <picture>
      <source type="image/avif" srcSet={art.avif} sizes={sizes} />
      <img
        src={art.src}
        srcSet={art.webp}
        sizes={sizes}
        width={art.width}
        height={art.height}
        alt=""
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        draggable={false}
        className={cx('select-none', className)}
      />
    </picture>
  )
}
